import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc, setDoc } from '../firebase.config';
import { db } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const CurrencySettings = () => {
  const { currentCompany } = useEnhancedCompany();
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Available currencies for selection
  const currencies = {
    'USD': { name: 'US Dollar', symbol: '$', country: 'United States' },
    'EUR': { name: 'Euro', symbol: '€', country: 'European Union' },
    'GBP': { name: 'British Pound', symbol: '£', country: 'United Kingdom' },
    'JPY': { name: 'Japanese Yen', symbol: '¥', country: 'Japan' },
    'INR': { name: 'Indian Rupee', symbol: '₹', country: 'India' },
    'CAD': { name: 'Canadian Dollar', symbol: 'C$', country: 'Canada' },
    'AUD': { name: 'Australian Dollar', symbol: 'A$', country: 'Australia' },
    'CHF': { name: 'Swiss Franc', symbol: 'CHF', country: 'Switzerland' },
    'CNY': { name: 'Chinese Yuan', symbol: '¥', country: 'China' },
    'AED': { name: 'UAE Dirham', symbol: 'د.إ', country: 'UAE' },
    'SAR': { name: 'Saudi Riyal', symbol: '﷼', country: 'Saudi Arabia' },
    'PKR': { name: 'Pakistani Rupee', symbol: '₨', country: 'Pakistan' },
    'BDT': { name: 'Bangladeshi Taka', symbol: '৳', country: 'Bangladesh' },
    'NPR': { name: 'Nepalese Rupee', symbol: '₨', country: 'Nepal' },
    'LKR': { name: 'Sri Lankan Rupee', symbol: '₨', country: 'Sri Lanka' },
    'MYR': { name: 'Malaysian Ringgit', symbol: 'RM', country: 'Malaysia' },
    'SGD': { name: 'Singapore Dollar', symbol: 'S$', country: 'Singapore' },
    'THB': { name: 'Thai Baht', symbol: '฿', country: 'Thailand' },
    'IDR': { name: 'Indonesian Rupiah', symbol: 'Rp', country: 'Indonesia' },
    'PHP': { name: 'Philippine Peso', symbol: '₱', country: 'Philippines' },
    'VND': { name: 'Vietnamese Dong', symbol: '₫', country: 'Vietnam' },
    'KRW': { name: 'South Korean Won', symbol: '₩', country: 'South Korea' },
    'HKD': { name: 'Hong Kong Dollar', symbol: 'HK$', country: 'Hong Kong' },
    'TWD': { name: 'Taiwan Dollar', symbol: 'NT$', country: 'Taiwan' },
    'BRL': { name: 'Brazilian Real', symbol: 'R$', country: 'Brazil' },
    'MXN': { name: 'Mexican Peso', symbol: '$', country: 'Mexico' },
    'ARS': { name: 'Argentine Peso', symbol: '$', country: 'Argentina' },
    'CLP': { name: 'Chilean Peso', symbol: '$', country: 'Chile' },
    'COP': { name: 'Colombian Peso', symbol: '$', country: 'Colombia' },
    'PEN': { name: 'Peruvian Sol', symbol: 'S/', country: 'Peru' },
    'ZAR': { name: 'South African Rand', symbol: 'R', country: 'South Africa' },
    'EGP': { name: 'Egyptian Pound', symbol: '£', country: 'Egypt' },
    'NGN': { name: 'Nigerian Naira', symbol: '₦', country: 'Nigeria' },
    'KES': { name: 'Kenyan Shilling', symbol: 'KSh', country: 'Kenya' },
    'GHS': { name: 'Ghanaian Cedi', symbol: '₵', country: 'Ghana' },
    'MAD': { name: 'Moroccan Dirham', symbol: 'د.م.', country: 'Morocco' },
    'TND': { name: 'Tunisian Dinar', symbol: 'د.ت', country: 'Tunisia' },
    'DZD': { name: 'Algerian Dinar', symbol: 'د.ج', country: 'Algeria' },
    'ETB': { name: 'Ethiopian Birr', symbol: 'Br', country: 'Ethiopia' },
    'UGX': { name: 'Ugandan Shilling', symbol: 'USh', country: 'Uganda' },
    'TZS': { name: 'Tanzanian Shilling', symbol: 'TSh', country: 'Tanzania' },
    'RWF': { name: 'Rwandan Franc', symbol: 'RF', country: 'Rwanda' },
    'BIF': { name: 'Burundian Franc', symbol: 'FBu', country: 'Burundi' },
    'DJF': { name: 'Djiboutian Franc', symbol: 'Fdj', country: 'Djibouti' },
    'SOS': { name: 'Somali Shilling', symbol: 'S', country: 'Somalia' },
    'ERN': { name: 'Eritrean Nakfa', symbol: 'Nfk', country: 'Eritrea' },
    'SDG': { name: 'Sudanese Pound', symbol: 'ج.س.', country: 'Sudan' },
    'SSP': { name: 'South Sudanese Pound', symbol: '£', country: 'South Sudan' },
    'CDF': { name: 'Congolese Franc', symbol: 'FC', country: 'Democratic Republic of Congo' },
    'AOA': { name: 'Angolan Kwanza', symbol: 'Kz', country: 'Angola' },
    'ZMW': { name: 'Zambian Kwacha', symbol: 'ZK', country: 'Zambia' },
    'BWP': { name: 'Botswana Pula', symbol: 'P', country: 'Botswana' },
    'SZL': { name: 'Swazi Lilangeni', symbol: 'L', country: 'Eswatini' },
    'LSL': { name: 'Lesotho Loti', symbol: 'L', country: 'Lesotho' },
    'NAD': { name: 'Namibian Dollar', symbol: 'N$', country: 'Namibia' },
    'MZN': { name: 'Mozambican Metical', symbol: 'MT', country: 'Mozambique' },
    'MWK': { name: 'Malawian Kwacha', symbol: 'MK', country: 'Malawi' },
    'ZWL': { name: 'Zimbabwean Dollar', symbol: 'Z$', country: 'Zimbabwe' }
  };

  useEffect(() => {
    loadCurrencySettings();
  }, [currentCompany]);

  const loadCurrencySettings = async () => {
    if (!currentCompany?.companyId) return;

    try {
      // First try to load from company settings default document
      const settingsRef = doc(db, `companies/${currentCompany.companyId}/settings/default`);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        setSelectedCurrency(data.currency || 'INR');
        setAvailableCurrencies(data.availableCurrencies || ['INR']);
      } else {
        // Fallback: check main company document for currency set by wizard
        const companyRef = doc(db, `companies/${currentCompany.companyId}`);
        const companySnap = await getDoc(companyRef);
        
        if (companySnap.exists()) {
          const companyData = companySnap.data();
          const currency = companyData.currency || companyData.details?.currency || 'INR';
          setSelectedCurrency(currency);
          setAvailableCurrencies([currency]);
          
          // Migrate to settings document for future use
          await setDoc(settingsRef, {
            currency: currency,
            availableCurrencies: [currency],
            updatedAt: new Date()
          });
        } else {
          // Set default values if no company exists
          setSelectedCurrency('INR');
          setAvailableCurrencies(['INR']);
        }
      }
    } catch (error) {
      console.error('Error loading currency settings:', error);
      // Set default values on error
      setSelectedCurrency('INR');
      setAvailableCurrencies(['INR']);
    }
  };

  const handleCurrencyChange = async (currencyCode) => {
    if (!currentCompany?.companyId) return;

    setLoading(true);
    try {
      // Save to company settings default document (same as language settings)
      const settingsRef = doc(db, `companies/${currentCompany.companyId}/settings/default`);
      await setDoc(settingsRef, {
        currency: currencyCode,
        availableCurrencies: availableCurrencies.length > 0 ? availableCurrencies : [currencyCode],
        updatedAt: new Date()
      }, { merge: true });

      setSelectedCurrency(currencyCode);
      setMessage('Currency updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating currency:', error);
      setMessage(`Error updating currency: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const addCurrency = (currencyCode) => {
    if (!availableCurrencies.includes(currencyCode)) {
      setAvailableCurrencies([...availableCurrencies, currencyCode]);
    }
  };

  const removeCurrency = (currencyCode) => {
    if (currencyCode === selectedCurrency) {
      setMessage('Cannot remove the default currency. Change default currency first.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    setAvailableCurrencies(availableCurrencies.filter(c => c !== currencyCode));
  };

  const saveAvailableCurrencies = async () => {
    if (!currentCompany?.companyId) return;

    setLoading(true);
    try {
      // Save to company settings default document (same as language settings)
      const settingsRef = doc(db, `companies/${currentCompany.companyId}/settings/default`);
      await setDoc(settingsRef, {
        currency: selectedCurrency,
        availableCurrencies: availableCurrencies,
        updatedAt: new Date()
      }, { merge: true });

      setMessage('Available currencies updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating available currencies:', error);
      setMessage(`Error updating currencies: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Multi-Currency Settings</h3>
        <p className="text-sm text-gray-600">
          Configure your company's default currency and available currencies for geographical expansion.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-md ${
          message.includes('Error') ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-green-50 border border-green-200 text-green-800'
        }`}>
          {message}
        </div>
      )}

      {/* Default Currency */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-gray-900 mb-3">Default Currency</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(currencies).map(([code, data]) => (
            <button
              key={code}
              onClick={() => handleCurrencyChange(code)}
              disabled={loading}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                selectedCurrency === code
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{code}</div>
                  <div className="text-sm text-gray-600">{data.name}</div>
                  <div className="text-xs text-gray-500">{data.country}</div>
                </div>
                <div className="text-lg font-bold">{data.symbol}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Available Currencies */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-gray-900 mb-3">Available Currencies</h4>
        <p className="text-sm text-gray-600 mb-4">
          Select currencies that will be available for your business operations.
        </p>
        
        <div className="space-y-2 mb-4">
          {availableCurrencies.map(currencyCode => (
            <div key={currencyCode} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-lg font-bold mr-3">{currencies[currencyCode]?.symbol}</span>
                <div>
                  <div className="font-medium">{currencyCode}</div>
                  <div className="text-sm text-gray-600">{currencies[currencyCode]?.name}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {selectedCurrency === currencyCode && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Default</span>
                )}
                <button
                  onClick={() => removeCurrency(currencyCode)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Add Currency</label>
          <AppSelect
            value=""
            onChange={(e) => {
              if (e.target.value) {
                addCurrency(e.target.value);
              }
            }}
            placeholder="Select currency to add"
            aria-label="Add currency"
            sheetTitle="Add currency"
            options={Object.entries(currencies)
              .filter(([code]) => !availableCurrencies.includes(code))
              .map(([code, data]) => ({
                value: code,
                label: `${code} - ${data.name} (${data.symbol})`,
              }))}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} w-full md:w-64`}
          />
        </div>

        <button
          onClick={saveAvailableCurrencies}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : 'Save Available Currencies'}
        </button>
      </div>

      {/* Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="font-medium text-blue-900 mb-2">💡 Multi-Currency Benefits</h5>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Expand your business to different geographical regions</li>
          <li>• Accept payments in local currencies</li>
          <li>• Generate reports in multiple currencies</li>
          <li>• Better customer experience for international clients</li>
          <li>• Currency converter tool available in navbar</li>
        </ul>
      </div>
    </div>
  );
};

export default CurrencySettings;
